import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '@/App';
import { Eye, EyeOff, LogIn, Factory, TrendingUp, Users, Shield, X, HelpCircle, Phone, Mail } from 'lucide-react';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useContext(AppContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      console.error('Login error:', err);
      // Better error message for different error types
      if (!err.response) {
        // Network error - no response from server
        setError('Network error. Please check your connection and try again.');
      } else if (err.response.status === 401) {
        setError(err.response.data?.detail || 'Invalid email or password.');
      } else {
        setError(err.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Factory, title: 'Production Management', desc: 'Track batches, plans & efficiency' },
    { icon: TrendingUp, title: 'Real-time Analytics', desc: 'Live dashboards & insights' },
    { icon: Users, title: 'HR & Workforce', desc: 'Manage employees & attendance' },
    { icon: Shield, title: 'Secure & Reliable', desc: 'Enterprise-grade security' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#1a237e] via-[#283593] to-[#1a237e] relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#e65100] rounded-full blur-[150px] opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#2e7d32] rounded-full blur-[150px] opacity-20 translate-x-1/3 translate-y-1/3"></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-white rounded-full blur-[100px] opacity-5 -translate-x-1/2 -translate-y-1/2"></div>
        </div>

        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 w-full">
          {/* Logo & Title */}
          <div className="mb-12">
            <div className="flex items-center space-x-4 mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-white rounded-2xl blur-md opacity-30"></div>
                <img 
                  src="/logo.jpg" 
                  alt="Swift Strips India" 
                  className="relative h-20 w-20 object-contain rounded-2xl shadow-2xl"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Swift Strips India</h1>
                <p className="text-blue-200 text-lg">Enterprise Resource Planning</p>
              </div>
            </div>
            
            {/* Tagline */}
            <div className="max-w-md">
              <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
                Streamline Your 
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#f57c00] via-white to-[#4caf50]">
                  Manufacturing
                </span>
                Operations
              </h2>
              <p className="text-blue-200 text-lg">
                Complete ERP solution for Production, Inventory, Dispatch, and Workforce Management.
              </p>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={idx}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/15 transition-all duration-300 group"
                >
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-gradient-to-br from-[#e65100] to-[#f57c00] rounded-lg shadow-lg group-hover:scale-110 transition-transform">
                      <Icon className="text-white" size={20} />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-sm">{feature.title}</h3>
                      <p className="text-blue-200 text-xs mt-0.5">{feature.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-blue-300 text-sm">
              © 2025 Swift Strips India. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logo.jpg" 
              alt="Swift Strips India" 
              className="h-20 w-20 object-contain mx-auto mb-4 rounded-xl shadow-lg"
            />
            <h1 className="text-2xl font-bold text-[#1a237e]">Swift Strips India</h1>
            <p className="text-gray-500">ERP System</p>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
            <p className="text-gray-500 mt-1">Sign in to your account to continue</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none transition-all duration-200 bg-white text-gray-900 placeholder-gray-400"
                data-testid="email-input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none transition-all duration-200 bg-white text-gray-900 placeholder-gray-400 pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-[#1a237e] focus:ring-[#1a237e]" />
                <span className="text-sm text-gray-600">Remember me</span>
              </label>
              <button 
                type="button" 
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-[#1a237e] hover:text-[#283593] font-medium"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start space-x-2" data-testid="error-message">
                <span className="text-red-500 mt-0.5">⚠</span>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#1a237e] via-[#283593] to-[#1a237e] hover:from-[#283593] hover:via-[#3949ab] hover:to-[#283593] text-white font-semibold py-4 px-4 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
              data-testid="login-button"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <LogIn size={20} />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Tricolor Bar */}
          <div className="flex items-center space-x-1 mt-8 justify-center">
            <div className="w-16 h-1 bg-gradient-to-r from-[#e65100] to-[#f57c00] rounded-full"></div>
            <div className="w-16 h-1 bg-gradient-to-r from-white to-gray-200 rounded-full border border-gray-200"></div>
            <div className="w-16 h-1 bg-gradient-to-r from-[#2e7d32] to-[#43a047] rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#1a237e] to-[#283593] p-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <HelpCircle size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Forgot Password?</h3>
                    <p className="text-blue-200 text-sm">We're here to help</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowForgotPassword(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-[#1a237e]" size={32} />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Contact Your Administrator
                </h4>
                <p className="text-gray-600 text-sm">
                  For security reasons, password resets are handled by your system administrator. 
                  Please contact them using the information below.
                </p>
              </div>
              
              {/* Contact Options */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="p-2 bg-[#1a237e] rounded-lg">
                    <Mail className="text-white" size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Email Administrator</p>
                    <p className="text-[#1a237e] font-semibold">admin@swiftstrips.com</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="p-2 bg-[#e65100] rounded-lg">
                    <Phone className="text-white" size={18} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Call Support</p>
                    <p className="text-gray-900 font-semibold">Contact your IT department</p>
                  </div>
                </div>
              </div>
              
              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>What will happen:</strong> Your administrator will reset your password 
                  and provide you with new login credentials.
                </p>
              </div>
              
              {/* Close Button */}
              <button
                onClick={() => setShowForgotPassword(false)}
                className="w-full bg-gradient-to-r from-[#1a237e] to-[#283593] text-white font-semibold py-3 px-4 rounded-xl hover:opacity-90 transition"
                data-testid="close-forgot-password-modal"
              >
                Got it, I'll contact admin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
